import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { addDays, format, parseISO } from "date-fns";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Navigation, Pagination, Autoplay } from "swiper/modules"; 
import "swiper/css/navigation";
import "swiper/css/pagination";
import useSEO from "../hooks/useSEO";

// Custom Integrated Calendar Component
import ApartmentAvailabilityCalendar from "../components/ApartmentAvailabilityCalendar";
import { calculateFeedbackStats, ratingEmoji } from "../utils/reviewStats";
import FeedbackWizard from "../components/FeedbackWizard";

// Icons
import { ChevronLeft, ChevronRight, ImageIcon, CheckCircle, Calendar, X, CreditCard, Star, Quote, MessageSquare, MapPin, BedDouble, Car } from "lucide-react";

// Context & API
import { TokenContext } from "../components/TokenContext";
import { callProxy } from "../api/api";
import PageHeader from "./PageHeader";
import { safeString } from "../utils/langUtils";
import { countryCodes as allCountryCodes } from "../utils/countryCodes";

/* =====================================================
   STATIC DATA FOR COMMENTS (Fallback if DB is empty)
===================================================== */
const STATIC_GUEST_REVIEWS = [
  { id: "s1", name: "Sarah Jenkins", date: "Feb 2026", rating: 10, comment: "Absolutely stunning apartment! Seamless check-in.", avatar: "S" },
  { id: "s2", name: "Michael Rossi", date: "Jan 2026", rating: 9, comment: "Great location and very professional service. Spotless!", avatar: "M" },
  { id: "s3", name: "Elena Sokolov", date: "Dec 2025", rating: 10, comment: "Perfect experience. Home away from home with 5-star service.", avatar: "E" }
];

const getDatesBetween = (start, end) => {
  const dates = [];
  let current = new Date(start);
  while (current < end) {
    dates.push(format(current, "yyyy-MM-dd"));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const PLACEHOLDER = "https://via.placeholder.com/1200x800.png?text=No+Image+Available";

const normalizeAssetPath = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  if (p.startsWith("/")) return p;
  return `/${p}`;
};

const UAE_TIMEZONE = "Asia/Dubai";
const CHECKIN_HOUR = 23; 

const getDubaiNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: UAE_TIMEZONE }));
const isAfterDubaiCheckinCutoff = () => getDubaiNow().getHours() >= CHECKIN_HOUR;

const RatingBar = ({ label, value }) => {
  const fiveStarValue = value / 2;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-20 md:w-24 text-slate-500 capitalize truncate">{label}</span>
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#3a2a00]" style={{ width: `${fiveStarValue * 20}%` }} />
      </div>
      <span className="text-slate-600 font-bold w-6 text-right">{fiveStarValue.toFixed(1)}</span>
    </div>
  );
};

export default function AllApartmentDetails() {
  const { slug } = useParams();
  const { token } = useContext(TokenContext);
  const navigate = useNavigate();
  
  const [apartment, setApartment] = useState(null);
  const [rateData, setRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState({ startDate: null, endDate: null });
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [hasSelectedDates, setHasSelectedDates] = useState(false);
  const [includeCleaning, setIncludeCleaning] = useState(false);
  const [countryCode, setCountryCode] = useState("+971");
  const [countryCodes, setCountryCodes] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);
  const [reviewStats, setReviewStats] = useState(null);
  const [bookingStep, setBookingStep] = useState(1); 



  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // RESTORED: Booking flow states
  const [openBookingFlow, setOpenBookingFlow] = useState(false);
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const [guestInfo, setGuestInfo] = useState({ fullName: "", email: "", phone: "", guests: 1 });
  const [errors, setErrors] = useState({});

  const afterCutoff = useMemo(() => isAfterDubaiCheckinCutoff(), []);
  const sameDayCheckinMessage = useMemo(() => afterCutoff ? "Same-day check-in closed." : "Same-day check-in available til 11:59 PM (UAE)", [afterCutoff]);

  const nextSlide = useCallback(() => {
    setLightboxIndex((prev) => (prev + 1) % lightboxImages.length);
  }, [lightboxIndex]);

  const prevSlide = useCallback(() => {
    setLightboxIndex((prev) => (prev - 1 + lightboxImages.length) % lightboxImages.length);
  }, [lightboxIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "Escape") setLightboxOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxOpen, nextSlide, prevSlide]);

  const handleCalendarChange = (data) => {
    if (data.available) {
      setSelection({ startDate: data.checkIn, endDate: data.checkOut });
      setHasSelectedDates(true);
      setOpenCalendar(false);
    }
  };

  useEffect(() => {
    const fetchApt = async () => {
      try {
        setLoading(true);
        const res = await callProxy(`/api/apartments/${slug}`, token);
        const data = res.data || res;
        setApartment({
          ...data,
          name: safeString(data.name),
          description: safeString(data.description),
          price_per_night: data.price_per_night ?? 0,
        });
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    fetchApt();
  }, [slug, token]);

  useEffect(() => {
    if (!apartment?.smoobuId) return;
    const fetchRates = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";
        const res = await fetch(`${API_BASE}/api/rates/${apartment.smoobuId}`);
        const json = await res.json();
        setRateData({ [String(apartment.smoobuId)]: json?.data?.[apartment.smoobuId] || json?.data || {} });
      } catch (err) { console.error(err); }
    };
    fetchRates();
  }, [apartment?.smoobuId]);

  useEffect(() => {
    if (!apartment) return;
    const fetchFeedbacks = async () => {
      try {
        const res = await callProxy(`/api/feedbacks/${apartment.smoobuId}`, token);
        setFeedbacks(res?.data || []);
        setReviewStats(calculateFeedbackStats(res?.data || []));
      } catch (err) { console.error(err); } finally { setLoadingFeedbacks(false); }
    };
    fetchFeedbacks();
  }, [apartment, token]);

  //========Country Code load ===========
  useEffect(() => {
  if (allCountryCodes && allCountryCodes.length > 0) {
    setCountryCodes(allCountryCodes);
  }
}, []);

  // const pricing = useMemo(() => {
  //   if (!hasSelectedDates || !selection.startDate || !selection.endDate || !rateData) return null;
  //   const dates = getDatesBetween(selection.startDate, selection.endDate);
  //   const rates = rateData[String(apartment?.smoobuId)];
  //   let subtotal = 0;
  //   for (const d of dates) {
  //     const dayRate = rates?.[d]?.price ?? apartment?.price_per_night ?? 0;
  //     subtotal += dayRate;
  //   }
  //   const vat = subtotal * 0.05;
  //   const cleaning = includeCleaning ? (apartment?.bedrooms >= 2 ? 350 : 250) : 0;
  //   return { nights: dates.length, subtotal, vat, cleaning, total: subtotal + vat + cleaning };
  // }, [rateData, selection, hasSelectedDates, apartment, includeCleaning]);


/* MODIFIED: Optimized pricing logic to handle 1-night stays and single-click previews */
const pricing = useMemo(() => {
  if (!selection.startDate || !apartment) return null;

  const smoobuId = String(apartment.smoobuId);
  const rates = rateData?.[smoobuId] || {};

  // If no checkout selected → assume 1 night
  const checkIn = format(selection.startDate, "yyyy-MM-dd");

  const checkOut = selection.endDate
    ? format(selection.endDate, "yyyy-MM-dd")
    : format(addDays(selection.startDate, 1), "yyyy-MM-dd");

  const dates = getDatesBetween(
    parseISO(checkIn),
    parseISO(checkOut)
  );

  if (!dates.length) return null;

  let subtotal = 0;

  for (const d of dates) {
    const dayRate =
      rates?.[d]?.price ||
      rates?.[d] || // ← IMPORTANT fallback if API returns direct number
      apartment.price_per_night ||
      0;

    subtotal += Number(dayRate);
  }

  if (subtotal <= 0) return null;

  const vat = subtotal * 0.05;
  const cleaning = includeCleaning
    ? apartment?.bedrooms >= 2 ? 350 : 250
    : 0;

  return {
    nights: dates.length,
    subtotal,
    vat,
    cleaning,
    total: subtotal + vat + cleaning,
  };

}, [selection, rateData, apartment, includeCleaning]);


  // const currentPriceDisplay = useMemo(() => {
  //   if (!apartment) return "Loading...";
  //   if (pricing) return `AED ${Math.round(pricing.total).toLocaleString()}`;
  //   const todayKey = format(getDubaiNow(), "yyyy-MM-dd");
  //   const todayRate = rateData?.[String(apartment.smoobuId)]?.[todayKey]?.price;
  //   return `AED ${(todayRate || apartment.price_per_night || 0).toLocaleString()}`;
  // }, [pricing, apartment, rateData]);

  /* MODIFIED: Consistent price rendering including VAT fallback */
const currentPriceDisplay = useMemo(() => {
    if (!apartment) return "Loading...";
    
    // 1. If user has interacted with calendar, show the calculated total
    if (pricing && pricing.total > 0) {
        return `AED ${Math.round(pricing.total).toLocaleString()}`;
    }

    // 2. Default View (No selection yet): Show Today's Rate + VAT
    const todayKey = format(getDubaiNow(), "yyyy-MM-dd");
    const baseRate = rateData?.[String(apartment.smoobuId)]?.[todayKey]?.price || apartment.price_per_night || 0;
    
    // We add 5% VAT to the base rate so it matches the final calculation style
    const baseWithVat = baseRate + (baseRate * 0.05);

    return baseWithVat > 0 
        ? `AED ${Math.round(baseWithVat).toLocaleString()}` 
        : "Contact us";
}, [pricing, apartment, rateData]);


  const handleStripePayment = async () => {
    try {
      setBookingSubmitting(true);
      const meta = {
        arrivalDate: format(selection.startDate, "dd-MM-yyyy"),
        departureDate: format(selection.endDate, "dd-MM-yyyy"),
        firstName: guestInfo.fullName.split(" ")[0],
        lastName: guestInfo.fullName.split(" ").slice(1).join(" ") || "",
        email: guestInfo.email,
        phone: `${countryCode}${guestInfo.phone}`,
        adults: String(guestInfo.guests),
        apartmentId: String(apartment?.smoobuId),
        price: String(pricing.total),
        bookingSource: "web",
      };
      const res = await callProxy("/api/create-checkout-session", token, { method: "POST", body: { apartmentId: meta.apartmentId, price: pricing.total, meta }});
      if (res?.data?.url) window.location.href = res.data.url;
    } catch (err) { setBookingSubmitting(false); }
  };


  const featuredGallery = useMemo(() => (apartment?.featuredImages || []).slice(0, 3).map(p => ({ url: normalizeAssetPath(p) })), [apartment]);
  const lightboxImages = useMemo(() => {
    const images = (apartment?.images || []).map(img => ({ url: normalizeAssetPath(img?.url) })).filter(i => i.url);
    const map = new Map();
    [...featuredGallery, ...images].forEach(img => map.set(img.url, img));
    return Array.from(map.values());
  }, [featuredGallery, apartment]);


  const handleContinue = () => {
    const newErrors = {};
    
    // Validate Full Name
    if (!guestInfo.fullName || !guestInfo.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    }
    
    // Validate Email
    if (!guestInfo.email || !guestInfo.email.trim()) {
      newErrors.email = "Email address is required";
    } else if (!/^\S+@\S+\.\S+$/.test(guestInfo.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Validate Phone
    if (!guestInfo.phone || guestInfo.phone.trim().length < 6) {
      newErrors.phone = "Valid phone number is required";
    }

    setErrors(newErrors);

    // Only proceed to Step 2 if there are zero errors
    if (Object.keys(newErrors).length === 0) {
      setBookingStep(2);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#3a2a00] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Link to="/apartments" className="text-sm text-slate-500 hover:text-[#3a2a00] mb-2 inline-flex items-center gap-1">← Back to listings</Link>
          <PageHeader title={apartment.name} subtitle={apartment.address} />
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8">
          
          <div className="space-y-8 min-w-0"> 
            <div className="hidden md:grid grid-cols-3 gap-2 h-[450px] rounded-2xl overflow-hidden shadow-sm border border-white">
              <div className="col-span-2 relative cursor-pointer overflow-hidden" onClick={() => {setLightboxIndex(0); setLightboxOpen(true);}}>
                <img src={featuredGallery[0]?.url || PLACEHOLDER} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Main" />
              </div>
              <div className="grid grid-rows-2 gap-2">
                {featuredGallery.slice(1, 3).map((img, i) => (
                  <div key={i} className="relative cursor-pointer overflow-hidden" onClick={() => {setLightboxIndex(i+1); setLightboxOpen(true);}}>
                    <img src={img.url} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" alt="Detail" />
                  </div>
                ))}
              </div>
            </div>

            <div className="md:hidden h-64 rounded-xl overflow-hidden shadow-sm" onClick={() => {setLightboxIndex(0); setLightboxOpen(true);}}>
               <img src={featuredGallery[0]?.url || PLACEHOLDER} className="w-full h-full object-cover" alt="Hero" />
            </div>

            <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-100 shadow-sm space-y-6">
              <h2 className="text-2xl font-bold text-slate-900">About this luxury home</h2>
              <p className={`text-slate-600 leading-relaxed ${showFullDescription ? "" : "line-clamp-4"}`}>{apartment.description}</p>
              <button onClick={() => setShowFullDescription(!showFullDescription)} className="text-blue-600 font-bold flex items-center gap-1">{showFullDescription ? "Read Less" : "Read More"} <ChevronRight className={`w-4 h-4 transition-transform ${showFullDescription ? "-rotate-90" : "rotate-90"}`} /></button>
            </div>

            {/* FIXED PART: REVIEWS SLIDER RENDERING FROM DATABASE */}
            <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-100 shadow-sm space-y-8 min-w-0">
              <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Star className="w-6 h-6 text-yellow-400 fill-yellow-400" /> Guest Experience</h2>
              <div className="pt-4 min-w-0">
                <Swiper
                  modules={[Autoplay, Pagination]}
                  spaceBetween={20}
                  slidesPerView={1}
                  autoplay={{ delay: 4500 }}
                  pagination={{ clickable: true }}
                  breakpoints={{ 768: { slidesPerView: 2 } }}
                  className="pb-10"
                >
                  {(feedbacks.length > 0 ? feedbacks : STATIC_GUEST_REVIEWS).map((rev, idx) => {
                    // Logic to calculate average score if it's a DB feedback
                    const displayRating = rev.rating || ((rev.cleanliness + rev.location + rev.value + rev.comfort) / 4);
                    const displayName = rev.name || "Verified Guest";
                    const displayDate = rev.createdAt ? format(new Date(rev.createdAt), "MMM yyyy") : rev.date;

                    return (
                      <SwiperSlide key={rev._id || idx}>
                        <div className="bg-slate-50 border border-slate-100 p-6 rounded-2xl h-full flex flex-col shadow-sm">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-[#3a2a00] text-white flex items-center justify-center font-bold">
                              {rev.avatar || displayName.charAt(0)}
                            </div>
                            <div>
                              <div className="text-sm font-bold text-slate-900">{displayName}</div>
                              <div className="text-[10px] text-slate-400 uppercase font-semibold">{displayDate}</div>
                            </div>
                            <div className="ml-auto text-xs font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md">
                              {displayRating.toFixed(1)}/10
                            </div>
                          </div>
                          <p className="text-slate-600 text-sm italic leading-relaxed line-clamp-4 flex-1">"{rev.comment || "Great stay, highly recommended!"}"</p>
                        </div>
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
              <FeedbackWizard apartmentName={apartment.name} onSubmit={() => {}} />
            </div>
          </div>

          <div className="relative">
            <div className="top-24 space-y-6">
            {/* CARD 1: Booking Card */}
              <div className="bg-white p-5 rounded-3xl border border-slate-100 space-y-6">
                <div>
                  <div className="text-3xl font-black text-[#3a2a00]">{currentPriceDisplay}</div>
                  <div className="text-xs font-semibold text-green-600 mt-1">
                    Price includes 5% VAT
                  </div>
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">{pricing ? "Total Price" : "Best Price Today"}</div>
                </div>

                {/* FIXED PART: CALENDAR ANCHORED POSITIONING */}
                <div className="relative">
                  <button onClick={() => setOpenCalendar(!openCalendar)} className="w-full flex items-center gap-4 p-4 border border-slate-200 rounded-2xl text-left hover:border-[#3a2a00] transition-all">
                    <Calendar className="w-5 h-5 text-slate-400" />
                    <div>
                       <div className="text-[10px] font-bold text-slate-400 uppercase">Dates</div>
                       <div className="text-sm font-bold text-slate-900">{hasSelectedDates ? `${format(selection.startDate, "dd MMM")} - ${format(selection.endDate, "dd MMM")}` : "Select Range"}</div>
                    </div>
                  </button>

                  {openCalendar && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 z-50 mt-2 bg-white rounded-2xl shadow-2xl border p-2 w-[320px] md:w-[350px]">

                       <ApartmentAvailabilityCalendar 
                       apartmentId={apartment.smoobuId} 
                       apartment={apartment}
                       icalUrl={apartment.ical_url} 
                       rateData={rateData} 
                       onChange={handleCalendarChange} />
                    </div>
                  )}
                </div>

                {pricing && (
                  <div className="bg-slate-50 p-4 rounded-2xl space-y-3 text-sm border border-slate-100">
                    <div className="flex justify-between"><span>{pricing.nights} Nights</span><span className="font-bold">AED {pricing.subtotal.toLocaleString()}</span></div>
                    <div className="flex justify-between"><span>VAT (5%)</span><span className="font-bold">AED {pricing.vat.toFixed(2)}</span></div>
                    <label className="flex items-center justify-between border-t pt-3 cursor-pointer">
                      <div className="flex items-center gap-2"><input type="checkbox" checked={includeCleaning} onChange={(e) => setIncludeCleaning(e.target.checked)} className="accent-[#3a2a00]" /><span className="text-xs">Include Cleaning</span></div>
                      <span className="font-bold">AED {apartment.bedrooms >= 2 ? 350 : 250}</span>
                    </label>
                    <div className="flex justify-between text-lg font-black border-t pt-3 text-[#3a2a00]"><span>Total</span><span>AED {pricing.total.toLocaleString()}</span></div>
                  </div>
                )}

                <button onClick={() => setOpenBookingFlow(true)} 
                disabled={!hasSelectedDates} className="w-full py-4 bg-[#3a2a00] text-white rounded-2xl font-bold shadow-lg hover:bg-black transition-all active:scale-95 disabled:opacity-50">
                  {hasSelectedDates ? "Reserve Property" : "Check Dates"}
                </button>
                <p className="text-[10px] text-center text-slate-400 font-medium italic">{sameDayCheckinMessage}</p>
              </div>
           
        
            {/* ADDED: HIGHLIGHTS CARD */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                    <h4 className="font-semibold text-slate-900 mb-3 text-sm">Property Highlights</h4>
                    <ul className="space-y-3">
                        <li className="flex gap-3 text-sm text-slate-600">
                            <MapPin className="w-4 h-4 text-[#3a2a00] flex-shrink-0" />
                            <span>Great location in {apartment.address?.split(',')[1] || "Dubai"}</span>
                        </li>
                        <li className="flex gap-3 text-sm text-slate-600">
                            <BedDouble className="w-4 h-4 text-[#3a2a00] flex-shrink-0" />
                            <span>Premium luxury bedding</span>
                        </li>
                        <li className="flex gap-3 text-sm text-slate-600">
                            <Car className="w-4 h-4 text-[#3a2a00] flex-shrink-0" />
                            <span>Free parking available</span>
                        </li>
                    </ul>
                </div>

                {/* ADDED: MAP CARD (FROM DATABASE) */}
                {apartment.map && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5">
                        <h4 className="font-semibold text-slate-900 mb-3 text-sm">
                            Property Location
                        </h4>

                        <div className="w-full h-[220px] rounded-xl overflow-hidden border border-slate-200 mb-3">
                            <iframe
                                title="Property Map"
                                src={apartment.map}
                                className="w-full h-full border-0"
                                loading="lazy"
                                allowFullScreen
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        </div>

                        <a
                            href={apartment.map}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs font-semibold text-[#3a2a00] hover:underline flex items-center gap-1"
                        >
                            View on Google Maps
                            <ChevronRight className="w-3 h-3" />
                        </a>
                    </div>
                )}
          </div>
        </div>
      </div>
    </main>

      {/* RESTORED: Booking Flow Modal with Stripe Logic */}

      {openBookingFlow && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 relative animate-fadeIn">

            {/* Close Button */}
            <button
              onClick={() => {
                setOpenBookingFlow(false);
                setBookingStep(1); // reset when closing
              }}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
            >
              <X />
            </button>

            {/* ===================== STEP 1 ===================== */}
            {bookingStep === 1 && (
              <div className="space-y-6">

                <div>
                  <h2 className="text-xl font-bold text-slate-900">
                    Reserve Your Stay
                  </h2>
                  <p className="text-sm text-slate-500">
                    Please provide your contact details to proceed.
                  </p>
                </div>

                <div className="space-y-4">

                  {/* Full Name */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 uppercase">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={guestInfo.fullName}
                      onChange={(e) => {
                        setGuestInfo({ ...guestInfo, fullName: e.target.value });
                        if (errors.fullName)
                          setErrors({ ...errors, fullName: null });
                      }}
                      className={`w-full border rounded-xl p-3 text-sm ${
                        errors.fullName
                          ? "border-red-500 bg-red-50"
                          : "border-slate-200"
                      }`}
                    />
                    {errors.fullName && (
                      <p className="text-[10px] text-red-500 font-bold">
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 uppercase">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      placeholder="email@example.com"
                      value={guestInfo.email}
                      onChange={(e) => {
                        setGuestInfo({ ...guestInfo, email: e.target.value });
                        if (errors.email)
                          setErrors({ ...errors, email: null });
                      }}
                      className={`w-full border rounded-xl p-3 text-sm ${
                        errors.email
                          ? "border-red-500 bg-red-50"
                          : "border-slate-200"
                      }`}
                    />
                    {errors.email && (
                      <p className="text-[10px] text-red-500 font-bold">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Phone */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 uppercase">
                      Phone Number *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value)}
                        className="border border-slate-200 rounded-xl p-3 text-sm w-24 bg-white"
                      >
                        {countryCodes.length > 0 ? (
                          countryCodes.map((c, i) => (
                            <option key={i} value={c.dial_code || c.code}>
                              {c.flag} {c.dial_code || c.code}
                            </option>
                          ))
                        ) : (
                          <option value="+971">+971</option>
                        )}
                      </select>

                      <input
                        type="tel"
                        placeholder="Phone"
                        value={guestInfo.phone}
                        onChange={(e) => {
                          setGuestInfo({
                            ...guestInfo,
                            phone: e.target.value.replace(/\D/g, ""),
                          });
                          if (errors.phone)
                            setErrors({ ...errors, phone: null });
                        }}
                        className={`flex-1 border rounded-xl p-3 text-sm ${
                          errors.phone
                            ? "border-red-500 bg-red-50"
                            : "border-slate-200"
                        }`}
                      />
                    </div>
                    {errors.phone && (
                      <p className="text-[10px] text-red-500 font-bold">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Guests */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-700 uppercase">
                      Total Guests
                    </label>
                    <input
                      type="number"
                      min={1}
                      value={guestInfo.guests}
                      onChange={(e) =>
                        setGuestInfo({
                          ...guestInfo,
                          guests: Number(e.target.value),
                        })
                      }
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm"
                    />
                  </div>
                </div>

                <button
                  onClick={handleContinue}
                  className="w-full py-4 bg-[#3a2a00] text-white rounded-xl font-bold hover:bg-black transition-all"
                >
                  Continue to Payment
                </button>

                <p className="text-[10px] text-slate-400 text-center uppercase tracking-widest">
                  Secure Checkout Powered by Stripe
                </p>
              </div>
            )}

            {/* ===================== STEP 2 ===================== */}
            {bookingStep === 2 && (
              <div className="space-y-6">

                <h2 className="text-xl font-bold text-slate-900">
                  Booking Summary
                </h2>

                <div className="bg-slate-50 p-4 rounded-xl text-sm space-y-2">
                  <p><strong>Name:</strong> {guestInfo.fullName}</p>
                  <p><strong>Email:</strong> {guestInfo.email}</p>
                  <p><strong>Phone:</strong> {countryCode} {guestInfo.phone}</p>
                  <p><strong>Guests:</strong> {guestInfo.guests}</p>

                  {selection?.startDate && selection?.endDate && (
                    <p>
                      <strong>Stay:</strong>{" "}
                      {format(selection.startDate, "dd MMM yyyy")} -{" "}
                      {format(selection.endDate, "dd MMM yyyy")}
                    </p>
                  )}

                  {pricing && (
                    <p className="font-bold pt-2 border-t">
                      Total: AED {pricing.total.toLocaleString()}
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setBookingStep(1)}
                    className="flex-1 py-3 border rounded-xl"
                  >
                    Back
                  </button>

                  <button
                    onClick={handleStripePayment}
                    disabled={bookingSubmitting}
                    className="flex-1 py-3 bg-green-600 text-white rounded-xl font-bold hover:bg-green-700 disabled:opacity-50"
                  >
                    {bookingSubmitting ? "Processing..." : "Confirm & Pay"}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}


      {/* FIXED PART: LIGHTBOX UI OVER IMAGE */}
      {lightboxOpen && (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-4">
          <div className="relative max-w-5xl w-full h-full flex flex-col items-center justify-center">
            
            {/* Close button strictly in the top right of the image container */}
            <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white bg-black/40 hover:bg-black/60 p-2 rounded-full z-[120] border border-white/20">
              <X className="w-6 h-6" />
            </button>

            {/* Navigation Arrows relative to image edges */}
            <button onClick={prevSlide} className="absolute left-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/20 hover:bg-black/50 z-[110]">
              <ChevronLeft className="w-8 h-8" />
            </button>

            <img src={lightboxImages[lightboxIndex]?.url} className="max-h-[85vh] max-w-full object-contain rounded-lg shadow-2xl animate-fadeIn" alt="Gallery" />
            
            <button onClick={nextSlide} className="absolute right-4 top-1/2 -translate-y-1/2 text-white p-2 rounded-full bg-black/20 hover:bg-black/50 z-[110]">
              <ChevronRight className="w-8 h-8" />
            </button>

            <div className="mt-4 text-white/80 text-sm font-bold bg-black/20 px-4 py-1 rounded-full">
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}